import { createApiFetch } from "../auth/apiFetch";
import { buildApiUrl, getApiBaseUrl } from "../runtimeConfig";
import { MeClient, RegisterAttributionRequest, SwaggerException } from "../web-api-client";

/**
 * Signup attribution (C1), Web side. Contract: POST /api/me/attribution.
 *
 * The app has no consent record of its own, so consent travels on the signup link: the site
 * (moneylights.ai) appends mlc=a (Analytics) or mlc=ap (Analytics + Advertising), plus
 * ref=<referrer hostname> and landing_path=<first page visited on the site>, and only forwards
 * utm_* / Google click ids it was allowed to. mlc is the ONLY
 * source of the consent flags. Without a valid mlc nothing is stored and nothing is sent; with
 * mlc=0 (the site's "no Analytics" signal) a pending entry is also deleted.
 *
 * Capture runs once at boot (src/index.jsx), before BrowserRouter reads the URL, so the router
 * never sees mlc/ref/landing_path. Sending runs from RequireAuth, the one place every sign-in path (email,
 * Google, Apple, the OAuth callback's returnUrl) passes through with a live session.
 */

export const SIGNUP_ATTRIBUTION_KEY = "ml-signup-attribution";

/**
 * Kill switch. While false nothing is read, stored or sent, even with a hand-crafted mlc: the
 * site params are still stripped from the address bar and any stored entry is deleted unsent.
 * Flipped to true in the final 'flip' step, together with the site gate MLC_ENABLED.
 * The withdrawal section in Settings does not depend on it: withdrawal must always work.
 */
export const ATTRIBUTION_CAPTURE_ENABLED = false;

let captureEnabled = ATTRIBUTION_CAPTURE_ENABLED;

/** Test hook: override the kill switch; no argument restores ATTRIBUTION_CAPTURE_ENABLED. */
export const setAttributionCaptureEnabledForTests = (enabled: boolean = ATTRIBUTION_CAPTURE_ENABLED) => {
  captureEnabled = enabled;
};

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;
const PLANS = new Set(["essential", "team", "enterprise"]);
const MAX_UTM_LENGTH = 200;
// Google Ads click ids, kept only with Advertising consent. fbclid and every other click id
// (msclkid, ttclid, ...) are never kept.
const ADVERTISING_CLICK_IDS = new Set(["gclid", "gbraid", "wbraid"]);

export type AttributionConsent = { analyticsConsent: boolean; advertisingConsent: boolean };

/** The raw mlc value, kept with the entry as proof of what was consented to (§11.1). */
export type ConsentMarker = "a" | "ap";

export type StoredSignupAttribution = AttributionConsent & {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  ref?: string;
  landingPath: string;
  plan?: string;
  consentMarker: ConsentMarker;
  capturedAt: string;
};

/**
 * mlc=0: the site saw a valid choice without Analytics (legal review §12.3). Not a consent value
 * but a clean-up signal: any pending entry is deleted, nothing is stored or sent.
 */
const WITHDRAWN_MARKER = "0";

/** mlc=a -> Analytics only, mlc=ap -> Analytics + Advertising; anything else is no consent. */
export const parseConsent = (mlc: string | null | undefined): AttributionConsent | null => {
  if (mlc === "a") {
    return { analyticsConsent: true, advertisingConsent: false };
  }
  if (mlc === "ap") {
    return { analyticsConsent: true, advertisingConsent: true };
  }
  return null;
};

const HOSTNAME = /^(?=.{1,253}$)[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)*$/;

/** ref must be a bare hostname: no scheme, port, path, user info or query. */
export const parseRefHost = (ref: string | null | undefined): string | undefined => {
  const value = ref?.trim().toLowerCase().replace(/\.$/, "");
  return value && HOSTNAME.test(value) ? value : undefined;
};

const MAX_LANDING_PATH_LENGTH = 500;
const hasControlChar = (value: string) =>
  Array.from(value).some((char) => char.charCodeAt(0) < 0x20 || char.charCodeAt(0) === 0x7f);

const isPathOnly = (value: string) =>
  value.startsWith("/") &&
  !value.startsWith("//") &&
  !/[\\?#]/.test(value) &&
  !value.includes("://") &&
  !hasControlChar(value);

/**
 * landing_path must be a local path and nothing else: no scheme or host (//, \, ://), no query,
 * no fragment, at most 500 chars. Its decoded form must pass too, so %2F%2F or %3F cannot
 * smuggle any of that in; a malformed escape is rejected.
 */
export const parseLandingPath = (value: string | null | undefined): string | undefined => {
  const path = value?.trim();
  if (!path || path.length > MAX_LANDING_PATH_LENGTH || !isPathOnly(path)) {
    return undefined;
  }
  try {
    return isPathOnly(decodeURIComponent(path)) ? path : undefined;
  } catch {
    return undefined;
  }
};

const CAPTURE_ROUTE = /(?:^|\/)(?:signup|register)\/?$/;
const CALLBACK_ROUTE = /\/(?:auth\/callback|auth-callback)(?:\/[^/]+)?\/?$/;
const RETURN_URL_KEYS = ["returnUrl", "redirect"];

const isLocalPath = (value: string | null): value is string =>
  Boolean(value && value.startsWith("/") && !value.startsWith("//"));

// What the site appends to the signup link for the app alone; never left in the address bar.
const SITE_PARAMS = ["mlc", "ref", "landing_path"];

const withoutConsentParams = (params: URLSearchParams) => {
  const next = new URLSearchParams(params);
  SITE_PARAMS.forEach((key) => next.delete(key));
  return next;
};

const toSearch = (params: URLSearchParams) => {
  const value = params.toString();
  return value ? `?${value}` : "";
};

/**
 * The landing page as path + query, keeping only what the visitor consented to (contract §2,
 * landingPath): the five utm_* keys as received, a valid plan, and gclid/gbraid/wbraid with
 * Advertising consent. mlc/ref/landing_path, fbclid and every other click id (msclkid, ttclid,
 * ...) never make it in. The path is the site's landing_path when it sent one, otherwise the
 * app page; the query always comes from the signup URL.
 *
 * At most 500 chars: params are dropped from the end, click ids first, never the path.
 */
const buildLandingPath = (path: string, params: URLSearchParams, consent: AttributionConsent) => {
  const kept: [string, string][] = [];
  params.forEach((value, key) => {
    const allowed =
      (UTM_KEYS as readonly string[]).includes(key) ||
      (key === "plan" && PLANS.has(value.trim().toLowerCase())) ||
      (ADVERTISING_CLICK_IDS.has(key) && consent.advertisingConsent);
    if (allowed) {
      kept.push([key, value]);
    }
  });
  const build = () => `${path}${toSearch(new URLSearchParams(kept))}`;
  let landingPath = build();
  while (landingPath.length > MAX_LANDING_PATH_LENGTH && kept.length > 0) {
    const lastClickId = kept.map(([key]) => ADVERTISING_CLICK_IDS.has(key)).lastIndexOf(true);
    kept.splice(lastClickId >= 0 ? lastClickId : kept.length - 1, 1);
    landingPath = build();
  }
  return landingPath;
};

const utm = (params: URLSearchParams, key: string) => {
  const value = params.get(key)?.trim();
  return value ? value.slice(0, MAX_UTM_LENGTH) : undefined;
};

/**
 * How long a pending entry waits for its sign-in. Past this, the visit that set it is no longer
 * this account's first touch: someone who abandoned /signup and months later signs in as Owner
 * of an organization that already existed must not send that old campaign as its first touch
 * (nor take the one slot the server's unique index allows).
 *
 * 7 days, not 30 (legal review §12.4 / §12.3 point 2): it also bounds the one case mlc=0 cannot
 * reach, someone who withdraws consent on the site and then opens the app directly.
 */
export const SIGNUP_ATTRIBUTION_TTL_DAYS = 7;
const SIGNUP_ATTRIBUTION_TTL_MS = SIGNUP_ATTRIBUTION_TTL_DAYS * 24 * 60 * 60 * 1000;

/** capturedAt must be a real instant, not in the future, and at most the TTL old. */
const isFresh = (capturedAt: unknown, now: Date) => {
  const captured = typeof capturedAt === "string" ? Date.parse(capturedAt) : NaN;
  const age = now.getTime() - captured;
  return Number.isFinite(age) && age >= 0 && age <= SIGNUP_ATTRIBUTION_TTL_MS;
};

export const readStoredAttribution = (now: () => Date = () => new Date()): StoredSignupAttribution | null => {
  try {
    const raw = localStorage.getItem(SIGNUP_ATTRIBUTION_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as StoredSignupAttribution;
    // The marker is authoritative (the server answers 400 to anything else, or to flags that
    // contradict it): an entry without a valid one is not ours, and the flags are re-derived.
    const consent = parseConsent(parsed?.consentMarker);
    if (!consent) {
      return null;
    }
    // Expired, unparseable or future-dated: delete it, so it is never sent and a new visit can
    // capture again.
    if (!isFresh(parsed.capturedAt, now())) {
      clearStoredAttribution();
      return null;
    }
    return { ...parsed, ...consent };
  } catch {
    return null;
  }
};

export const clearStoredAttribution = () => {
  try {
    localStorage.removeItem(SIGNUP_ATTRIBUTION_KEY);
  } catch {
    // ignore storage errors
  }
};

/**
 * Reads mlc/ref/landing_path on /signup (and /register), or inside the returnUrl of the OAuth
 * callback, strips them from the address bar, and stores the first touch when mlc is valid.
 * mlc=0 deletes a pending entry instead, switch on or off. Any other value (forged, empty) is
 * ignored as before: it neither stores nor clears, since §12 only asks the site's mlc=0 to clear.
 *
 * history.replaceState keeps history.state as is (React Router's key/idx/usr live there) and
 * the hash, and runs before the router starts, so the router's location is the clean URL.
 */
export const captureSignupAttribution = (now: () => Date = () => new Date()) => {
  if (!captureEnabled) {
    // Switched off: an entry left by an earlier build must never be sent.
    clearStoredAttribution();
  }
  const { pathname, search, hash } = window.location;
  const isCaptureRoute = CAPTURE_ROUTE.test(pathname);
  if (!isCaptureRoute && !CALLBACK_ROUTE.test(pathname)) {
    return;
  }

  const outer = new URLSearchParams(search);
  const returnUrlKey = RETURN_URL_KEYS.find((key) => isLocalPath(outer.get(key)));
  const inner = returnUrlKey ? new URL(outer.get(returnUrlKey)!, window.location.origin) : null;

  // Strip first, whatever mlc says: a forged or empty mlc leaves the bar just as clean.
  const hasConsentParams = (params: URLSearchParams) => SITE_PARAMS.some((key) => params.has(key));
  if (hasConsentParams(outer) || (inner && hasConsentParams(inner.searchParams))) {
    const nextOuter = withoutConsentParams(outer);
    if (returnUrlKey && inner) {
      nextOuter.set(returnUrlKey, `${inner.pathname}${toSearch(withoutConsentParams(inner.searchParams))}${inner.hash}`);
    }
    try {
      window.history.replaceState(window.history.state, "", `${pathname}${toSearch(nextOuter)}${hash}`);
    } catch {
      // ignore: a URL we cannot rewrite still must not break the page
    }
  }

  // Clean-up is always allowed, so this runs before the kill switch. Either place counts, and it
  // wins over a valid mlc in the other: withdrawal must never be outvoted.
  const withdrawn =
    (isCaptureRoute && outer.get("mlc") === WITHDRAWN_MARKER) ||
    inner?.searchParams.get("mlc") === WITHDRAWN_MARKER;
  if (withdrawn) {
    clearStoredAttribution();
    return;
  }

  if (!captureEnabled) {
    return;
  }

  // Where the attribution sits: the page's own query (the site's /signup link), or a local
  // returnUrl (the Google/Apple round trip brings the landing URL back that way).
  const outerConsent = isCaptureRoute ? parseConsent(outer.get("mlc")) : null;
  const innerConsent = inner ? parseConsent(inner.searchParams.get("mlc")) : null;
  const consent = outerConsent ?? innerConsent;
  if (!consent || readStoredAttribution(now)) {
    return;
  }
  const source = outerConsent ? outer : inner!.searchParams;
  const consentMarker: ConsentMarker = consent.advertisingConsent ? "ap" : "a";
  const appPath = outerConsent ? pathname : inner!.pathname;

  const plan = source.get("plan")?.trim().toLowerCase();
  const entry: StoredSignupAttribution = {
    utmSource: utm(source, "utm_source"),
    utmMedium: utm(source, "utm_medium"),
    utmCampaign: utm(source, "utm_campaign"),
    utmTerm: utm(source, "utm_term"),
    utmContent: utm(source, "utm_content"),
    ref: parseRefHost(source.get("ref")),
    // The site's first page when it sent one, otherwise the app page the visitor landed on; the
    // consented utm_*/plan/click ids of the signup URL ride along either way.
    landingPath: buildLandingPath(parseLandingPath(source.get("landing_path")) ?? appPath, source, consent),
    plan: plan && PLANS.has(plan) ? plan : undefined,
    ...consent,
    consentMarker,
    capturedAt: now().toISOString(),
  };

  try {
    localStorage.setItem(SIGNUP_ATTRIBUTION_KEY, JSON.stringify(entry));
  } catch {
    // ignore storage errors
  }
};

const SEND_TIMEOUT_MS = 8000;

// Per page load: one attempt per stored entry, however often RequireAuth mounts.
const attempted = new Set<string>();
let inFlight: Promise<void> | null = null;

/** Test hook: forget the per-page-load guard. */
export const resetSignupAttributionSendState = () => {
  attempted.clear();
  inFlight = null;
};

type PrimaryOrganizationCheck = "owner" | "not-owner" | "none" | "unavailable";

const checkPrimaryOrganization = async (
  apiFetch: ReturnType<typeof createApiFetch>,
): Promise<PrimaryOrganizationCheck> => {
  try {
    const response = await apiFetch(buildApiUrl("/api/organizations"), {
      method: "GET",
      headers: { Accept: "application/json" },
    });
    if (!response.ok) {
      return "unavailable";
    }
    const payload = (await response.json()) as { organizations?: { isPrimary?: boolean; role?: string }[] };
    const primary = payload.organizations?.find((org) => org.isPrimary);
    if (!primary) {
      return "none";
    }
    return primary.role?.trim().toLowerCase() === "owner" ? "owner" : "not-owner";
  } catch {
    return "unavailable";
  }
};

/** The POST body for a stored entry: the marker, and the two flags derived from it. */
const toRegisterAttributionRequest = (entry: StoredSignupAttribution) =>
  new RegisterAttributionRequest({
    utmSource: entry.utmSource,
    utmMedium: entry.utmMedium,
    utmCampaign: entry.utmCampaign,
    utmTerm: entry.utmTerm,
    utmContent: entry.utmContent,
    referrer: entry.ref,
    landingPath: entry.landingPath,
    plan: entry.plan,
    source: "local_storage",
    consentMarker: entry.consentMarker,
    ...parseConsent(entry.consentMarker),
  });

const send = async (entry: StoredSignupAttribution) => {
  const apiFetch = createApiFetch(SEND_TIMEOUT_MS);

  // Invited members are never the Owner (CreateTeamMember rejects that role): their visit is
  // not the organization's first touch. Drop the entry without sending.
  const organization = await checkPrimaryOrganization(apiFetch);
  if (organization === "not-owner") {
    clearStoredAttribution();
    return;
  }
  if (organization !== "owner") {
    // No primary org yet, or the check failed: keep the entry for the next sign-in.
    return;
  }

  try {
    await new MeClient(getApiBaseUrl(), { fetch: apiFetch }).registerSignupAttribution(
      toRegisterAttributionRequest(entry),
    );
    clearStoredAttribution();
  } catch (error) {
    // 401/404 (no session, no primary org yet) keep the entry for the next sign-in, and so
    // does a request that never got an answer. Any other answer is final: re-sending is a
    // no-op server-side and must not turn into a loop.
    if (error instanceof SwaggerException && error.status !== 401 && error.status !== 404) {
      clearStoredAttribution();
    }
  }
};

/**
 * Sends the stored first touch once, after sign-in. Fire-and-forget: never throws, never
 * awaited by navigation, and a no-op (no request at all) when nothing is stored.
 */
export const sendPendingSignupAttribution = (now: () => Date = () => new Date()): Promise<void> => {
  if (!captureEnabled) {
    clearStoredAttribution();
    return Promise.resolve();
  }
  if (inFlight) {
    return inFlight;
  }
  const entry = readStoredAttribution(now);
  if (!entry || attempted.has(entry.capturedAt)) {
    return Promise.resolve();
  }
  attempted.add(entry.capturedAt);
  inFlight = send(entry)
    .catch(() => undefined)
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
};
